/**
 * @fileoverview Command Palette (Cmd+K / Ctrl+K) para busca rápida global.
 *
 * Busca incremental com debounce de 300ms em:
 * - Páginas da aplicação (filtro client-side instantâneo)
 * - Leads, clientes e membros (via Supabase)
 *
 * Resultados agrupados por tipo com badges coloridos.
 * Suporta Cmd+Shift+F como atalho alternativo.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Users, BarChart3, Lightbulb, Kanban, Briefcase, Target, Settings, Layers, User, MessageCircle, Contact, Loader2, Clock, Trash2 } from 'lucide-react';
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator } from '@/components/ui/command';
import { supabase } from '@/integrations/supabase/client';
import { useStore } from '@/store/useStore';
import { stageLabel } from '@/lib/stageLabel';
import { Badge } from '@/components/ui/badge';

interface SearchResult {
  id: string;
  title: string;
  subtitle?: string;
  icon: typeof Search;
  path: string;
  type: 'page' | 'member' | 'lead' | 'client';
}

const PAGES: SearchResult[] = [
  { id: 'dashboard', title: 'Dashboard', icon: BarChart3, path: '/dashboard', type: 'page' },
  { id: 'highlights', title: 'Highlights', subtitle: 'Análise executiva com IA', icon: Lightbulb, path: '/highlights', type: 'page' },
  { id: 'pipeline', title: 'Pipeline', icon: Kanban, path: '/pipeline', type: 'page' },
  { id: 'leads', title: 'Leads', icon: Users, path: '/leads', type: 'page' },
  { id: 'equipe', title: 'Equipe', icon: Users, path: '/equipe', type: 'page' },
  { id: 'clientes', title: 'Clientes', icon: Briefcase, path: '/clientes', type: 'page' },
  { id: 'metas', title: 'Metas', icon: Target, path: '/metas', type: 'page' },
  { id: 'whatsapp', title: 'WhatsApp', icon: MessageCircle, path: '/whatsapp', type: 'page' },
  { id: 'workspaces', title: 'Workspaces', icon: Layers, path: '/workspaces', type: 'page' },
  { id: 'config', title: 'Configurações', icon: Settings, path: '/configuracoes', type: 'page' },
];

// ── Simple debounce hook ─────────────────────────────────────────────────────
function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ── Recent selections (localStorage) ────────────────────────────────────────
// Persist the last 5 things the user opened from the palette so we can offer
// them as a "Recentes" group when the input is empty. Keyed by id+type so a
// page and a lead with the same id never collide.
const RECENT_KEY = 'crm:cmdk:recent:v1';
const RECENT_LIMIT = 5;

interface RecentItem {
  id: string;
  title: string;
  subtitle?: string;
  path: string;
  type: SearchResult['type'];
  at: number;
}

function loadRecents(): RecentItem[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, RECENT_LIMIT) : [];
  } catch {
    return [];
  }
}

function pushRecent(item: SearchResult): RecentItem[] {
  try {
    const existing = loadRecents();
    const next: RecentItem[] = [
      { id: item.id, title: item.title, subtitle: item.subtitle, path: item.path, type: item.type, at: Date.now() },
      ...existing.filter(r => !(r.id === item.id && r.type === item.type)),
    ].slice(0, RECENT_LIMIT);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    return next;
  } catch {
    return loadRecents();
  }
}

function clearRecents() {
  try { localStorage.removeItem(RECENT_KEY); } catch { /* empty */ }
}

// Per-type icon fallback when restoring a recent item whose original icon
// component isn't directly serializable. Pages can be re-resolved from the
// PAGES list; everything else uses these defaults.
const ICON_BY_TYPE: Record<SearchResult['type'], typeof Search> = {
  page: Search,
  member: User,
  lead: Contact,
  client: Briefcase,
};

const TYPE_LABEL: Record<SearchResult['type'], string> = {
  page: 'Página',
  member: 'Pessoa',
  lead: 'Lead',
  client: 'Cliente',
};

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [members, setMembers] = useState<SearchResult[]>([]);
  const [leads, setLeads] = useState<SearchResult[]>([]);
  const [clients, setClients] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [recents, setRecents] = useState<RecentItem[]>(() => loadRecents());
  const { activeProjectId, activeWorkspaceId } = useStore();
  const navigate = useNavigate();
  const location = useLocation();

  // Debounced query — 300ms delay for fast responsiveness without hammering Supabase
  const debouncedQuery = useDebounce(inputValue.trim(), 300);

  // ⌘K / Ctrl+K — main palette shortcut.
  // ⌘+Shift+F     — alternate (advanced search alias).
  // "/"           — Linear/GitHub-style focus shortcut: open palette when
  //                 the user is *not* already typing somewhere. We bail on
  //                 input/textarea/contentEditable so "/" still types
  //                 literally inside text fields.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(o => !o);
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'f') {
        e.preventDefault();
        setOpen(o => !o);
        return;
      }
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const t = e.target as HTMLElement | null;
        const typing = !!t && (
          t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' ||
          t.tagName === 'SELECT' || t.isContentEditable
        );
        if (typing) return;
        // Prefer the page-level search input when present (Pipeline /
        // Leads tag their input with data-page-search). Falls back to
        // opening the palette so the shortcut always does *something*.
        const pageSearch = document.querySelector('[data-page-search]') as HTMLInputElement | null;
        if (pageSearch) {
          e.preventDefault();
          pageSearch.focus();
          // Move cursor to end so further typing extends current search.
          try { pageSearch.setSelectionRange(pageSearch.value.length, pageSearch.value.length); } catch { /* empty */ }
          return;
        }
        e.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Reset input when palette closes; refresh recents from storage when it opens
  // (in case another tab/component pushed something between opens).
  useEffect(() => {
    if (!open) {
      setInputValue('');
    } else {
      setRecents(loadRecents());
    }
  }, [open]);

  // Initial members load — fires on every palette open and isn't gated by the
  // query, so the "Pessoas" group is browseable even with an empty input.
  // The query-time member filter (below, in doSearch) overrides this set
  // when the user starts typing.
  useEffect(() => {
    if (!open || !activeWorkspaceId) return;
    let mounted = true;
    (async () => {
      const { data: wm } = await supabase.from('workspace_members')
        .select('user_id, profiles!workspace_members_user_id_profiles_fkey(id, name, department)')
        .eq('workspace_id', activeWorkspaceId)
        .limit(20);
      if (!mounted || !wm) return;
      setMembers(wm.map((m: any) => ({
        id: m.profiles.id,
        title: m.profiles.name,
        subtitle: m.profiles.department,
        icon: User,
        path: '/equipe',
        type: 'member' as const,
      })));
    })();
    return () => { mounted = false; };
  }, [open, activeWorkspaceId]);

  // ── Incremental search against Supabase ─────────────────────────────────────
  useEffect(() => {
    if (!open || !activeProjectId) return;

    let mounted = true;
    const q = debouncedQuery;
    // Don't fetch everything when query is empty — wait for user input
    if (!q) { setSearching(false); return; }
    const likePattern = `%${q}%`;

    // Tag-search modifier: a leading "#" switches lead/client queries from
    // name/company ilike to a tags-array contains filter. e.g. "#hot" finds
    // every lead/client tagged "hot".
    const tagMode = q.startsWith('#');
    const tagFilter = tagMode ? q.slice(1).trim() : '';

    setSearching(true);

    const doSearch = async () => {
      const [leadsRes, clientsRes] = await Promise.all([
        tagMode && tagFilter
          ? supabase.from('leads')
              .select('id, contact_name, company, column_id, tags')
              .eq('project_id', activeProjectId)
              .is('deleted_at', null)
              .contains('tags', [tagFilter])
              .limit(30)
          : supabase.from('leads')
              .select('id, contact_name, company, column_id')
              .eq('project_id', activeProjectId)
              .is('deleted_at', null)
              .or(q ? `contact_name.ilike.${likePattern},company.ilike.${likePattern}` : 'id.neq.00000000-0000-0000-0000-000000000000')
              .limit(q ? 20 : 50),

        tagMode && tagFilter
          ? supabase.from('clients')
              .select('id, contact_name, company, status, tags')
              .eq('project_id', activeProjectId as string)
              .is('deleted_at', null)
              .contains('tags', [tagFilter])
              .limit(20)
          : supabase.from('clients')
              .select('id, contact_name, company, status')
              .eq('project_id', activeProjectId as string)
              .is('deleted_at', null)
              .or(q ? `contact_name.ilike.${likePattern},company.ilike.${likePattern}` : 'id.neq.00000000-0000-0000-0000-000000000000')
              .limit(q ? 20 : 30),
      ]);

      if (!mounted) return;

      if (leadsRes.data) {
        setLeads(leadsRes.data.map((l: any) => ({
          id: l.id, title: `${l.contact_name} — ${l.company}`, subtitle: stageLabel(l.column_id),
          icon: Contact, path: '/pipeline', type: 'lead' as const,
        })));
      }
      if (clientsRes.data) {
        setClients(clientsRes.data.map((c: any) => ({
          id: c.id, title: `${c.contact_name} — ${c.company}`, subtitle: c.status,
          icon: Briefcase, path: '/clientes', type: 'client' as const,
        })));
      }

      // Members: when there's a query, refetch and filter by name. The
      // empty-query case is handled by a dedicated effect (above) so the
      // "Pessoas" group is browseable from open.
      if (q && activeWorkspaceId) {
        const { data: wm } = await supabase.from('workspace_members')
          .select('user_id, profiles!workspace_members_user_id_profiles_fkey(id, name, department)')
          .eq('workspace_id', activeWorkspaceId).limit(20);
        if (wm) {
          const filtered = wm.filter((m: any) =>
            m.profiles.name?.toLowerCase().includes(q.toLowerCase())
          );
          setMembers(filtered.map((m: any) => ({
            id: m.profiles.id, title: m.profiles.name, subtitle: m.profiles.department,
            icon: User, path: '/equipe', type: 'member' as const,
          })));
        }
      }

      setSearching(false);
    };

    doSearch();
    return () => { mounted = false; };
  }, [open, debouncedQuery, activeProjectId, activeWorkspaceId]);

  const handleSelect = useCallback((result: SearchResult) => {
    setRecents(pushRecent(result));
    setOpen(false);
    navigate(result.path);
  }, [navigate]);

  const handleClearRecents = useCallback(() => {
    clearRecents();
    setRecents([]);
  }, []);

  // Filter PAGES client-side (instant no debounce needed)
  const filteredPages = useMemo(() => {
    if (!inputValue.trim()) return PAGES;
    const q = inputValue.toLowerCase();
    return PAGES.filter(p => p.title.toLowerCase().includes(q) || p.subtitle?.toLowerCase().includes(q));
  }, [inputValue]);

  const TYPE_COLORS: Record<string, string> = {
    page: 'bg-secondary/10 text-secondary',
    member: 'bg-info/10 text-info',
    lead: 'bg-chart-6/10 text-chart-6',
    client: 'bg-chart-7/10 text-chart-7',
  };

  const hasQuery = inputValue.trim().length > 0;
  const currentPath = location.pathname;

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <div className="relative">
        <CommandInput
          placeholder="Buscar páginas, leads, clientes, pessoas… (#tag para filtrar por tag)"
          value={inputValue}
          onValueChange={setInputValue}
        />
        {searching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
      {inputValue.startsWith('#') && (
        <div className="px-3 py-1.5 text-[11px] text-muted-foreground border-b border-border bg-muted/30">
          Filtrando leads e clientes pela tag <span className="font-mono font-semibold text-primary">{inputValue}</span>
        </div>
      )}
      <CommandList>
        <CommandEmpty>
          {searching ? 'Buscando…' : 'Nenhum resultado encontrado.'}
        </CommandEmpty>

        {hasQuery === false && recents.length > 0 && (
          <CommandGroup heading="Recentes">
            {recents.map(r => {
              const Icon = PAGES.find(p => p.id === r.id && r.type === 'page')?.icon ?? ICON_BY_TYPE[r.type];
              const result: SearchResult = { id: r.id, title: r.title, subtitle: r.subtitle, path: r.path, type: r.type, icon: Icon };
              return (
                <CommandItem
                  key={`recent-${r.type}-${r.id}`}
                  onSelect={() => handleSelect(result)}
                  className="gap-3 cursor-pointer"
                >
                  <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="flex-1 truncate">{r.title}</span>
                  <Badge variant="outline" className={`text-[10px] ${TYPE_COLORS[r.type]}`}>{TYPE_LABEL[r.type]}</Badge>
                </CommandItem>
              );
            })}
            <CommandItem
              onSelect={handleClearRecents}
              className="gap-3 cursor-pointer text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1 text-[11px]">Limpar histórico</span>
            </CommandItem>
          </CommandGroup>
        )}

        {filteredPages.length > 0 && (
          <>
            {hasQuery === false && recents.length > 0 && <CommandSeparator />}
            <CommandGroup heading={hasQuery ? `Páginas (${filteredPages.length})` : 'Páginas'}>
              {filteredPages.map(page => {
                const isActive = page.path === currentPath;
                return (
                  <CommandItem key={page.id} onSelect={() => handleSelect(page)} className="gap-3 cursor-pointer">
                    <page.icon className={`h-4 w-4 shrink-0 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
                    <span className={`flex-1 ${isActive ? 'font-semibold' : ''}`}>{page.title}</span>
                    {isActive && <Badge variant="outline" className="text-[10px] bg-success/10 text-success border-success/30">Atual</Badge>}
                    {!isActive && page.subtitle && <span className="text-[11px] text-muted-foreground">{page.subtitle}</span>}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </>
        )}

        {leads.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={`Leads (${leads.length})`}>
              {leads.map(lead => (
                <CommandItem key={lead.id} onSelect={() => handleSelect(lead)} className="gap-3 cursor-pointer">
                  <lead.icon className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="flex-1 truncate">{lead.title}</span>
                  <Badge variant="outline" className={`text-[11px] ${TYPE_COLORS.lead}`}>{lead.subtitle}</Badge>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {clients.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={`Clientes (${clients.length})`}>
              {clients.map(client => (
                <CommandItem key={client.id} onSelect={() => handleSelect(client)} className="gap-3 cursor-pointer">
                  <client.icon className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="flex-1 truncate">{client.title}</span>
                  <Badge variant="outline" className={`text-[11px] ${TYPE_COLORS.client}`}>{client.subtitle}</Badge>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {members.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={hasQuery ? `Pessoas (${members.length})` : 'Pessoas'}>
              {members.map(member => (
                <CommandItem key={member.id} onSelect={() => handleSelect(member)} className="gap-3 cursor-pointer">
                  <member.icon className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="flex-1 truncate">{member.title}</span>
                  {member.subtitle && <Badge variant="outline" className={`text-[11px] ${TYPE_COLORS.member}`}>{member.subtitle}</Badge>}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>

      <div className="border-t border-border px-3 py-1.5 flex items-center justify-between text-[10px] text-muted-foreground gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 rounded border border-border bg-muted font-mono text-[10px]">↑↓</kbd> navegar
          </span>
          <span className="inline-flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 rounded border border-border bg-muted font-mono text-[10px]">↵</kbd> abrir
          </span>
          <span className="inline-flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 rounded border border-border bg-muted font-mono text-[10px]">esc</kbd> fechar
          </span>
        </div>
        <span className="hidden sm:inline">
          Dica: <code className="font-mono px-1 py-0.5 rounded bg-muted">#tag</code> filtra por tag
        </span>
      </div>
    </CommandDialog>
  );
}
